import moment from 'moment'
import React, { useContext, useEffect, useMemo, useRef, useState} from 'react'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'
import { ProductsContext, StatisticsContext } from '../../context'
import TableChart from './TableChart'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'

window.bootstrap = require('bootstrap/dist/js/bootstrap.bundle.js');

const StatisticsV2 = () => {
    
  const {
    days,
    statisticsLoaded,
    fetchStatistics,
    statisticsUpdatedAt
  } = useContext(StatisticsContext)

  const {
    resorts,
    resortsLoaded,
    fetchResorts,
    domains,
    domainsLoaded,
    fetchDomains,
    durations,
    durationsLoaded,
    fetchDurations,
    genericProducts,
    genericProductsLoaded,
    specificProducts,
    specificProductsLoaded,
    fetchProducts
  } = useContext(ProductsContext)

  useEffect(() => {
    if(!resortsLoaded) {
      fetchResorts()
    }
  }, [resortsLoaded, fetchResorts])

  useEffect(() => {
    if(!domainsLoaded) {
      fetchDomains()
    }
  }, [domainsLoaded, fetchDomains])

  useEffect(() => {
    if(!durationsLoaded) {
      fetchDurations()
    }
  }, [durationsLoaded, fetchDurations])

  useEffect(() => {
    if(!genericProductsLoaded) {
      fetchProducts('generic')
    }
  }, [genericProductsLoaded, fetchProducts])

  useEffect(() => {
    if(!specificProductsLoaded) {
      fetchProducts('specific')
    }
  }, [specificProductsLoaded, fetchProducts])

  const rangeOptions = [{
    label: 'Last 7 Days',
    value: 'last_7_days',
    start: moment().subtract(6, 'days').toDate(),
    end: moment().toDate()
  }, {
    label: 'Last 30 Days',
    value: 'last_30_days',
    start: moment().subtract(29, 'days').toDate(),
    end: moment().toDate()
  }, {
    label: 'Current Month',
    value: 'current_month',
    start: moment().startOf('month').toDate(),
    end: moment().endOf('month').toDate()
  }, {
    label: 'Last Month',
    value: 'last_month',
    start: moment().subtract(1, 'month').startOf('month').toDate(),
    end: moment().subtract(1, 'month').endOf('month').toDate()
  }, {
    label: 'Year',
    value: 'year',
    start: moment().startOf('year').toDate(),
    end: moment().endOf('year').toDate()
  }, {
    label: 'All Time',
    value: 'all',
    start: moment().subtract(29, 'days').toDate(),
    end: moment().toDate()
  }, {
    label: 'Custom Range',
    value: 'custom',
    start: moment().subtract(29, 'days').toDate(),
    end: moment().toDate()
  }]

  const metricOptions = [
    { label: 'Purchases', value: 'purchases' },
    { label: 'Revenue', value: 'amount' },
  ]

  const comparisonOptions = [
    { label: 'No Comparison', value: 0 },
    { label: 'Last Year', value: 1 },
    { label: 'Last 2 Years', value: 2 },
  ]

  const [selectedMetric, setSelectedMetric] = useState('purchases') // purchases, revenue
  const [selectedRange, setSelectedRange] = useState(rangeOptions.find(r => r.value === 'last_30_days')!)
  const [comparisonYears, setComparisonYears] = useState(0) // 0 = none, 1 = last year, 2 = last 2 years
  const [barInterval, setBarInterval] = useState('day') // day, week, month
  const [customRange, setCustomRange] = useState({start: moment().subtract(29, 'days').toDate(), end: moment().toDate()})
  const [resortsPopularityData, setResortsPopularityData] = useState<{ label: string, count: number, amount: number }[]>([])
  const [domainsPopularityData, setDomainsPopularityData] = useState<{ label: string, count: number, amount: number }[]>([])
  const [durationsPopularityData, setDurationsPopularityData] = useState<{ label: string, count: number, amount: number }[]>([])
  const [languagePopularityData, setLanguagePopularityData] = useState<{ label: string, count: number, amount: number }[]>([])
  const [genericProductsPopularityData, setGenericProductsPopularityData] = useState<{ label: string, count: number, amount: number }[]>([])
  const [specificProductsPopularityData, setSpecificProductsPopularityData] = useState<{ label: string, count: number, amount: number }[]>([])
  const [bestSellersData, setBestSellersData] = useState<{
    resort: {
      id: string, label: string, count: number, amount: number
    },
    domain: {
      id: string, label: string, count: number, amount: number
    },
    duration: {
      id: string, label: string, count: number, amount: number
    },
    genericProduct: {
      id: string, label: string, count: number, amount: number
    },
    specificProduct: {
      id: string, label: string, count: number, amount: number
    },
    combo: {
      resort: { id: string, label: string },
      domain: { id: string, label: string },
      duration: { id: string, label: string },
      genericProduct: { id: string, label: string },
      specificProduct: { id: string, label: string },
      count: number, amount: number
    }
  }[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if(!statisticsLoaded) {
      fetchStatistics()
    }
  }, [statisticsLoaded, fetchStatistics])

  useEffect(() => {
    if(!statisticsLoaded) return
  }, [days, statisticsLoaded])

  useEffect(() => {
    // adjust bar interval based on selected range
    const rangeStart = selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.start : selectedRange.start
    const rangeEnd = selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.end : selectedRange.end
    if(!rangeStart || !rangeEnd) return
    const rangeDays = moment(rangeEnd).diff(moment(rangeStart), 'days') + 1
    if(rangeDays > 60 && barInterval === 'day') {
      setBarInterval('month')
    }
    if(rangeDays > 365) {
      setComparisonYears(0)
    }
  }, [selectedRange, barInterval, customRange])

  useEffect(() => {
    if(selectedRange.value === 'all') {
      const minDate = days[0] ? moment(days[0].date, 'YYYY-MM-DD').toDate() : moment().subtract(29, 'days').toDate()
      setCustomRange({start: minDate, end: moment().toDate()})
    }
  }, [selectedRange, days])

  const rangeAggregate = useMemo(() => {
    if(!days || days.length === 0) return { purchases: 0, amount: 0 }
    const rangeStart = selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.start : selectedRange.start
    const rangeEnd = selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.end : selectedRange.end
    if(!rangeStart || !rangeEnd) return { purchases: 0, amount: 0 }

    let totalPurchases = 0
    let totalAmount = 0

    let date = rangeStart
    while(date <= rangeEnd) {
      const dayData = days.find((d: any) => d.date === moment(date).format('YYYY-MM-DD'))
      if(dayData) {
        totalPurchases += dayData.purchases - (dayData.refunds || 0)
        totalAmount += dayData.amount - (dayData.refund_amount || 0)
      }
      date = moment(date).add(1, 'day').toDate()
    }

    return {
      purchases: totalPurchases,
      amount: totalAmount
    }
  }, [days, selectedRange, customRange])

  const rangeStart = useMemo(() => {
    return selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.start : selectedRange.start
  }, [selectedRange, customRange])

  const rangeEnd = useMemo(() => {
    return selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.end : selectedRange.end
  }, [selectedRange, customRange])

  const chartRef = useRef(null)

  useEffect(() => {
    const refreshChart = () => {
      if (!chartRef.current) {
        return
      }
  
      const height = parseInt(getCSS(chartRef.current, 'height'))

      const series: {data: number[], name: string}[] = []
      for(let i = comparisonYears; i >= 0; i--) {
        series.push({ data: [], name: `${metricOptions.find(m => m.value === selectedMetric)?.label} ${new Date().getFullYear() - i}` } )
      }
      
      const xAxisLabels = []   
      
      const rangeStart = (selectedRange.value === 'custom' || selectedRange.value === 'all') ? customRange.start : selectedRange.start
      const rangeEnd = (selectedRange.value === 'custom' || selectedRange.value === 'all') ? customRange.end : selectedRange.end

      let date = rangeStart

      let currentInterval = null
      let currentIntervalLabel = ''

      while(date <= rangeEnd) {

        if(barInterval === 'day') {
          xAxisLabels.push(moment(date).format('DD/MM'))
        } else if(barInterval === 'week') {
          const week = moment(date).week()
          if(currentInterval !== week) {
            currentInterval = week
            currentIntervalLabel = `W${week}`
          }
          xAxisLabels.push(currentIntervalLabel)
        } else if(barInterval === 'month') {
          const month = moment(date).month()
          if(currentInterval !== month) {
            currentInterval = month
            currentIntervalLabel = moment(date).format('MMM')
            if(rangeEnd.getFullYear() !== rangeStart.getFullYear()) {
              currentIntervalLabel += ` '${moment(date).format('YY')}`
            }
          }
          xAxisLabels.push(currentIntervalLabel)
        }

        for(let i = comparisonYears; i >= 0; i--) {
          const yearDate = moment(date).subtract(i, 'year')
          const dayData = days.find((d: any) => d.date === yearDate.format('YYYY-MM-DD'))
          // series[comparisonYears - i].data.push(dayData ? dayData[selectedMetric] : 0)
          if(barInterval === 'day') {
            let value = dayData ? dayData[selectedMetric] : 0
            if(selectedMetric === 'amount') {
              value = value / 100
            }
            series[comparisonYears - i].data.push(value)
          } else if(barInterval === 'week') {
            // aggregate weekly
            const weekStart = moment(yearDate).startOf('week').format('YYYY-MM-DD')
            const weekEnd = moment(yearDate).endOf('week').format('YYYY-MM-DD')
            const weekData = days.filter((d: any) => d.date >= weekStart && d.date <= weekEnd)
            let weekSum = weekData.reduce((sum: number, d: any) => sum + d[selectedMetric], 0)
            if(selectedMetric === 'amount') {
              weekSum = weekSum / 100
            }
            series[comparisonYears - i].data.push(weekSum)
            date = moment(date).endOf('week').toDate() // jump to end of week
          } else if(barInterval === 'month') {
            // aggregate monthly
            const monthStart = moment(yearDate).startOf('month').format('YYYY-MM-DD')
            const monthEnd = moment(yearDate).endOf('month').format('YYYY-MM-DD')
            const monthData = days.filter((d: any) => d.date >= monthStart && d.date <= monthEnd)
            // const monthSum = monthData.reduce((sum: number, d: any) => sum + d[selectedMetric], 0)
            let monthSum = monthData.reduce((sum: number, d: any) => sum + d[selectedMetric], 0)
            if(selectedMetric === 'amount') {
              monthSum = monthSum / 100
            }
            series[comparisonYears - i].data.push(monthSum)
            date = moment(date).endOf('month').toDate() // jump to end of month
          }
        }
        date = moment(date).add(1, 'day').toDate()
      }
      // console.log(data)
      // populate data based on selected range


      const chartType = 'bar'
      const baseColor = getCSSVariableValue('--kt-primary')
      const lightColor = getCSSVariableValue('--kt-primary-light')
      const yFormatter = selectedMetric === 'amount' ? (val: any) => `${(val).toFixed(2)}€` : (val: any) => val

      const chart = new ApexCharts(chartRef.current, getChartOptions(series, xAxisLabels, yFormatter, height))
      if (chart) {
        chart.render()
      }
  
      return chart
    }

    const chart = refreshChart()
    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, days, selectedRange, customRange, selectedMetric, comparisonYears, barInterval])

  useEffect(() => {
    if(!resortsLoaded) return
    const resortCounts: { [key: string]: { count: number, amount: number } } = {}
    const domainCounts: { [key: string]: { count: number, amount: number } } = {}
    const durationCounts: { [key: string]: { count: number, amount: number } } = {}
    const languageCounts: { [key: string]: { count: number, amount: number } } = {}
    const genericProductCounts: { [key: string]: { count: number, amount: number } } = {}
    const specificProductCounts: { [key: string]: { count: number, amount: number } } = {}
    const comboCounts: { [key: string]: { count: number, amount: number } } = {} // key is combination of resort-domain-duration-genericProduct ids

    const rangeStart = selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.start : selectedRange.start
    const rangeEnd = selectedRange.value === 'custom' || selectedRange.value === 'all' ? customRange.end : selectedRange.end

    let date = rangeStart

    while(date <= rangeEnd) {
      const dayData = days.find((d: any) => d.date === moment(date).format('YYYY-MM-DD'))

      for(const key in dayData?.resort) {
        if(!resortCounts[key]) {
          resortCounts[key] = { count: 0, amount: 0 }
        }
        resortCounts[key].count += dayData.resort[key].purchases - (dayData.resort[key].refunds || 0)
        resortCounts[key].amount += dayData.resort[key].amount - (dayData.resort[key].refund_amount || 0)
      }
      for(const key in dayData?.domain) {
        if(!domainCounts[key]) {
          domainCounts[key] = { count: 0, amount: 0 }
        }
        domainCounts[key].count += dayData.domain[key].purchases - (dayData.domain[key].refunds || 0)
        domainCounts[key].amount += dayData.domain[key].amount - (dayData.domain[key].refund_amount || 0)
      }
      for(const key in dayData?.duration) {
        if(!durationCounts[key]) {
          durationCounts[key] = { count: 0, amount: 0 }
        }
        durationCounts[key].count += dayData.duration[key].purchases - (dayData.duration[key].refunds || 0)
        durationCounts[key].amount += dayData.duration[key].amount - (dayData.duration[key].refund_amount || 0)
      }
      for(const key in dayData?.language) {
        if(!languageCounts[key]) {
          languageCounts[key] = { count: 0, amount: 0 }
        }
        languageCounts[key].count += dayData.language[key].purchases - (dayData.language[key].refunds || 0)
        languageCounts[key].amount += dayData.language[key].amount - (dayData.language[key].refund_amount || 0)
      }
      for(const key in dayData?.product_generic) {
        if(!genericProductCounts[key]) {
          genericProductCounts[key] = { count: 0, amount: 0 }
        }
        genericProductCounts[key].count += dayData.product_generic[key].purchases - (dayData.product_generic[key].refunds || 0)
        genericProductCounts[key].amount += dayData.product_generic[key].amount - (dayData.product_generic[key].refund_amount || 0)
      }
      for(const key in dayData?.product_specific) {
        if(!specificProductCounts[key]) {
          specificProductCounts[key] = { count: 0, amount: 0 }
        }
        specificProductCounts[key].count += dayData.product_specific[key].purchases - (dayData.product_specific[key].refunds || 0)
        specificProductCounts[key].amount += dayData.product_specific[key].amount - (dayData.product_specific[key].refund_amount || 0)
      }

      date = moment(date).add(1, 'day').toDate()
    }

    const resortsData: { label: string, count: number, amount: number }[] = []
    for(const key in resortCounts) {
      const resort = resorts.find((r: any) => r.id === key)
      if(!resort) continue
      resortsData.push({
        label: resort.name.fr,
        count: resortCounts[key].count,
        amount: resortCounts[key].amount
      })
    }
    resortsData.sort((a, b) => {
      if(a.count < b.count) return 1
      if(a.count > b.count) return -1
      
      if(a.label.toLowerCase() < b.label.toLowerCase()) return 1
      if(a.label.toLowerCase() > b.label.toLowerCase()) return -1

      return 0
    })

    const domainsData: { label: string, count: number, amount: number }[] = []
    for(const key in domainCounts) {
      const domain = domains.find((d: any) => d.id === key)
      if(!domain) continue
      domainsData.push({
        label: domain.title.fr,
        count: domainCounts[key].count,
        amount: domainCounts[key].amount
      })
    }
    domainsData.sort((a, b) => {
      if(a.count < b.count) return 1
      if(a.count > b.count) return -1
      
      if(a.label.toLowerCase() < b.label.toLowerCase()) return 1
      if(a.label.toLowerCase() > b.label.toLowerCase()) return -1

      return 0
    })

    const durationsData: { label: string, count: number, amount: number }[] = []
    for(const key in durationCounts) {
      const duration = durations.find((d: any) => d.id === key)
      if(!duration) continue
      durationsData.push({
        label: duration.title.fr,
        count: durationCounts[key].count,
        amount: durationCounts[key].amount
      })
    }
    durationsData.sort((a, b) => {
      if(a.count < b.count) return 1
      if(a.count > b.count) return -1
      
      if(a.label.toLowerCase() < b.label.toLowerCase()) return 1
      if(a.label.toLowerCase() > b.label.toLowerCase()) return -1

      return 0
    })

    const languagesData: { label: string, count: number, amount: number }[] = []
    for(const key in languageCounts) {
      let label = 'Other'
      if(key === 'fr') label = 'French'
      else if(key === 'en') label = 'English'
      else if(key === 'it') label = 'Italian'
      else if(key === 'es') label = 'Spanish'
      languagesData.push({
        label,
        count: languageCounts[key].count,
        amount: languageCounts[key].amount
      })
    }
    languagesData.sort((a, b) => {
      if(a.count < b.count) return 1
      if(a.count > b.count) return -1
      
      if(a.label.toLowerCase() < b.label.toLowerCase()) return 1
      if(a.label.toLowerCase() > b.label.toLowerCase()) return -1

      return 0
    })

    const genericProductsData : { label: string, count: number, amount: number }[] = []
    const specificProductsData : { label: string, count: number, amount: number }[] = []
    for(const key in genericProductCounts) {
      const genericProduct = genericProducts.find((p: any) => p.id === key)
      genericProductsData.push({
        label: genericProduct?.name.fr || key,
        count: genericProductCounts[key].count,
        amount: genericProductCounts[key].amount
      })
    }
    for(const key in specificProductCounts) {
      const specificProduct = specificProducts.find((p: any) => p.id === key)
      specificProductsData.push({
        label: specificProduct?.name.fr || key,
        count: specificProductCounts[key].count,
        amount: specificProductCounts[key].amount
      })
    }
    genericProductsData.sort((a, b) => b.count - a.count)
    specificProductsData.sort((a, b) => b.count - a.count)

    
    setDomainsPopularityData(domainsData)
    setDurationsPopularityData(durationsData)
    setLanguagePopularityData(languagesData)
    setResortsPopularityData(resortsData)
    setGenericProductsPopularityData(genericProductsData)
    setSpecificProductsPopularityData(specificProductsData)

  }, [days, selectedRange, customRange, comparisonYears, resorts, resortsLoaded])

  const renderEurAmount = (amountInCents: number) => {
    // split number to euros and cents, format as XXX,YYY.ZZ€
    const euros = Math.floor(amountInCents / 100)
    const cents = Math.abs(amountInCents % 100)
    return `${euros.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${cents.toString().padStart(2, "0")}€`
  }

  const onRefreshStatistics = async () => {
    setLoading(true)
    try {
      await fetchStatistics({ force: true })
    } catch (error) {
      alert('Error rebuilding analytics: ' + error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if(!window.bootstrap) return
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    Array.from(tooltipTriggerList).map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl))
  }, [window.bootstrap])

  return (
    <>
      <ToolbarCustom pageTitle={'Statistics'} loading={!statisticsLoaded} filterElements={[
        // render updated at date
        <div className='fw-semibold fs-7 text-muted'>Last updated at: {moment(statisticsUpdatedAt).format('DD/MM/YYYY HH:mm')}</div>,
        // render refresh button
        <button 
          data-bs-toggle='tooltip'
          data-bs-trigger='hover'
          // data-toggle='tooltip'
          // data-placement='top'
          // data-trigger='hover'
          title='Build statistics from latest data, this may take a while. Rebuilding statistics consumes a lot of resources, please avoid doing it too often. Statistics are automatically rebuilt every night.'
          className='btn btn-sm btn-primary btn-loading' onClick={onRefreshStatistics} disabled={loading || !statisticsLoaded}>
          <span className='indicator-progress' style={{display: 'block'}}>
            {loading ? 'Please wait... ' : 'Refresh Statistics'}
            {loading && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
          </span>
        </button>
      ]} />
      <div className='row g-5 g-xl-8 mt-1'>
        <div className='col-8'>
          <div className='card'>
            <div className='card-header border-0'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>{rangeStart.toLocaleDateString()} - {rangeEnd.toLocaleDateString()}</span>
              </h3>
              {/* display aggregate of purchases and revenue for selected range */}
              <div className='card-toolbar'>
                <div className='d-flex flex-column text-end'>
                  <span className='fw-semibold fs-7 text-muted'>Purchases</span>
                  <span className='fw-bold fs-3 text-dark'>{rangeAggregate.purchases}</span>
                  {/* calculate percentage difference from previous years */}
                  {/* for each comparison year, render a line with : "Compared to YYYY: +X% / -X%" */}
                  {/* percentage difference is calculated as: ((current - previous) / previous) * 100 */}
                  {/* percentage is rounded to 2 decimal places */}
                  {/* percentage is green if positive, red if negative */}
                  {comparisonYears > 0 && (
                    <span className='fw-semibold fs-7 text-muted'>
                      {Array.from({ length: comparisonYears }, (_, i) => {
                        const year = i + 1
                        // calculate previous range aggregate
                        let previousTotal = 0
                        let date = moment(rangeStart).subtract(year, 'year').toDate()
                        const previousRangeEnd = moment(rangeEnd).subtract(year, 'year').toDate()
                        while(date <= previousRangeEnd) {
                          const dayData = days.find((d: any) => d.date === moment(date).format('YYYY-MM-DD'))
                          if(dayData) {
                            previousTotal += dayData.purchases - (dayData.refunds || 0)
                          }
                          date = moment(date).add(1, 'day').toDate()
                        }
                        const currentTotal = rangeAggregate.purchases
                        const difference = previousTotal === 0 ? Infinity : ((currentTotal - previousTotal) / previousTotal) * 100
                        const differenceRounded = difference.toFixed(2)
                        return (
                          <div key={year}>
                            {new Date().getFullYear() - year}: {previousTotal} <span style={{ color: difference >= 0 ? 'green' : 'red' }}>{difference >= 0 ? '+' : ''}{differenceRounded}%</span>
                          </div>
                        )
                      })}                      
                    </span>
                  )}
                </div>
                <div className='d-flex flex-column text-end ms-6'>
                  <span className='fw-semibold fs-7 text-muted'>Revenue</span>
                  <span className='fw-bold fs-3 text-dark'>{renderEurAmount(rangeAggregate.amount)}</span>
                  {comparisonYears > 0 && (
                    <span className='fw-semibold fs-7 text-muted'>
                      {Array.from({ length: comparisonYears }, (_, i) => {
                        const year = i + 1
                        // calculate previous range aggregate
                        let previousTotal = 0
                        let date = moment(rangeStart).subtract(year, 'year').toDate()
                        const previousRangeEnd = moment(rangeEnd).subtract(year, 'year').toDate()
                        while(date <= previousRangeEnd) {
                          const dayData = days.find((d: any) => d.date === moment(date).format('YYYY-MM-DD'))
                          if(dayData) {
                            previousTotal += dayData.amount - (dayData.refund_amount || 0)
                          }
                          date = moment(date).add(1, 'day').toDate()
                        }
                        const currentTotal = rangeAggregate.amount
                        const difference = previousTotal === 0 ? Infinity : ((currentTotal - previousTotal) / previousTotal) * 100
                        const differenceRounded = difference.toFixed(2)
                        return (
                          <div key={year}>
                            {new Date().getFullYear() - year}: {renderEurAmount(previousTotal)} <span style={{ color: difference >= 0 ? 'green' : 'red' }}>{difference >= 0 ? '+' : ''}{differenceRounded}%</span>
                          </div>
                        )
                      })}                      
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='card-body card-xxl-stretch'>
            <div ref={chartRef} style={{height: '500px'}}></div>
          </div>
        </div>
        <div className='col-4'>
          {/* render filter selectors */}
          <div className='card card-xl-stretch mb-5 mb-xl-8'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Filters</span>
                {/* <span className='text-muted fw-semibold fs-7'>Adjust the statistics</span> */}
              </h3>
            </div>
            <div className='card-body pt-5'>
              <div className='mb-10'>
                <label className='form-label fw-semibold'>Chart Metric</label>
                <select 
                  className='form-select form-select-solid'
                  value={selectedMetric} 
                  onChange={(e) => setSelectedMetric(e.target.value)}
                >
                  {metricOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className='mb-10'>
                <label className='form-label fw-semibold'>Date Range</label>
                <select 
                  className='form-select form-select-solid'
                  value={selectedRange.label} 
                  onChange={(e) => {
                    const range = rangeOptions.find(r => r.label === e.target.value)
                    if(range) {
                      setSelectedRange(range)
                    }
                  }}
                >
                  {rangeOptions.map((option) => (
                    <option key={option.label} value={option.label}>{option.label}</option>
                  ))}
                </select>
              </div>
              {selectedRange.value === 'custom' && (
                <div className='mb-10'>
                  <label className='form-label fw-semibold'>Custom Range</label>
                  <div className='d-flex'>
                    <input 
                      type='date' 
                      className='form-control form-control-solid me-3'
                      value={moment(customRange.start).format('YYYY-MM-DD')}
                      max={moment().format('YYYY-MM-DD')}
                      onChange={(e) => {
                        const newStart = moment(e.target.value, 'YYYY-MM-DD').toDate()
                        if(newStart > customRange.end) {
                          // setCustomRange({start: newStart, end: newStart})
                          setCustomRange({start: newStart, end: moment(newStart).add(1, 'day').toDate()})
                        } else {
                          setCustomRange({start: newStart, end: customRange.end})
                        }
                      }}
                    />
                    <input 
                      type='date' 
                      className='form-control form-control-solid'
                      value={moment(customRange.end).format('YYYY-MM-DD')}
                      max={moment().format('YYYY-MM-DD')}
                      onChange={(e) => {
                        const newEnd = moment(e.target.value, 'YYYY-MM-DD').toDate()
                        if(newEnd < customRange.start) {
                          // setCustomRange({start: newEnd, end: newEnd})
                          setCustomRange({start: moment(newEnd).subtract(1, 'day').toDate(), end: newEnd})
                        } else {
                          setCustomRange({start: customRange.start, end: newEnd})
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <div className='mb-10'>
                <label className='form-label fw-semibold'>Bar Interval</label>
                <select 
                  className='form-select form-select-solid'
                  value={barInterval} 
                  onChange={(e) => setBarInterval(e.target.value)}
                >
                  <option value='day' disabled={moment(rangeEnd).diff(moment(rangeStart), 'days') > 60}>Day</option>
                  <option value='week' disabled={moment(rangeEnd).diff(moment(rangeStart), 'days') < 7}>Week</option>
                  <option value='month' disabled={moment(rangeEnd).diff(moment(rangeStart), 'days') < 30}>Month</option>
                </select>
              </div>
              <div className='mb-10'>
                <label className='form-label fw-semibold'>Comparison</label>
                <select 
                  className='form-select form-select-solid'
                  value={comparisonYears} 
                  onChange={(e) => setComparisonYears(parseInt(e.target.value))}
                  disabled={selectedRange.value === 'all' || (selectedRange.value === 'custom' && moment(rangeEnd).diff(moment(rangeStart), 'days') > 365)}
                >
                  {comparisonOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-6'>
          <TableChart
            id="generic-products"
            className='card-xl-stretch mb-xl-8'
            title="Generic Products"
            subtitle="Purchases per generic product"
            data={genericProductsPopularityData}
          />
        </div>
        <div className='col-xl-6'>
          <TableChart
            id="specific-products"
            className='card-xl-stretch mb-xl-8'
            title="Specific Products"
            subtitle="Purchases per specific product"
            data={specificProductsPopularityData}
          />
        </div>
      </div>
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-6'>
          <TableChart
            id="resorts"
            className='card-xl-stretch mb-xl-8'
            title="Resorts"
            subtitle="Purchases per resort"
            data={resortsPopularityData}
          />
        </div>
        <div className='col-xl-6'>
          <TableChart
            id="domains"
            className='card-xl-stretch mb-xl-8'
            title="Activities"
            subtitle="Purchases per activity"
            data={domainsPopularityData}
          />
        </div>
      </div>
      
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-6'>
          <TableChart
            id="durations"
            className='card-xl-stretch mb-xl-8'
            title="Durations"
            subtitle="Purchases per duration"
            data={durationsPopularityData}
          />
        </div>
        <div className='col-xl-6'>
          <TableChart
            id="languages"
            className='card-xl-stretch mb-xl-8'
            title="Languages"
            subtitle="Purchases per language"
            data={languagePopularityData}
          />
        </div>
      </div>
    </>
  )
}

export default StatisticsV2

function getChartOptions(series: any[], xAxisLabels: any[], yFormatter: (val: any) => string, height: number): ApexOptions {
  
  const labelColor = getCSSVariableValue('--kt-gray-500')
  const borderColor = getCSSVariableValue('--kt-gray-200')
  const primaryColor = getCSSVariableValue('--kt-primary')
  const secondaryColor = getCSSVariableValue('--kt-info')
  const tertiaryColor = getCSSVariableValue('--kt-warning')

  const colors = [primaryColor, secondaryColor, tertiaryColor]

  return {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '80%',
        borderRadius: 5,
      },
    },
    legend: {
      show: true,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
    },
    xaxis: {
      categories: xAxisLabels,
      // tickAmount: 0,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      floating: false,
      decimalsInFloat: 0,
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: yFormatter
      },
    },
    colors: series.map((s, i) => colors[i]).reverse(),
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}
